import * as React from "react"
import { useTranslation } from "react-i18next"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cn } from "../../lib/utils"
import { modalOverlayClassName } from "../../lib/overlay"
import { isReactSelectOutsideEvent } from "../../lib/react-select-portal"

function isDialogDescriptionElement(el: React.ReactElement): boolean {
 const type = el.type as { displayName?: string }
 return (
 el.type === DialogPrimitive.Description
 || type?.displayName === DialogPrimitive.Description.displayName
 )
}

function isDialogTitleElement(el: React.ReactElement): boolean {
 const type = el.type as { displayName?: string }
 return (
 el.type === DialogPrimitive.Title
 || type?.displayName === DialogPrimitive.Title.displayName
 )
}

function containsDialogChild(
 node: React.ReactNode,
 predicate: (el: React.ReactElement) => boolean
): boolean {
 return React.Children.toArray(node).some((child) => {
 if (!React.isValidElement(child)) return false
 if (predicate(child)) return true
 if (child.type === React.Fragment) {
 return containsDialogChild((child.props as { children?: React.ReactNode }).children, predicate)
 }
 const props = child.props as { children?: React.ReactNode }
 if (props?.children != null) return containsDialogChild(props.children, predicate)
 return false
 })
}

/** Radix warns if there is no `DialogTitle`/`DialogDescription`; recurse so we do not add duplicates. */
function containsDialogTitle(node: React.ReactNode): boolean {
 return containsDialogChild(node, isDialogTitleElement)
}

function containsDialogDescription(node: React.ReactNode): boolean {
 return containsDialogChild(node, isDialogDescriptionElement)
}

const Dialog = ({
 modal = true,
 ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & { modal?: boolean }) => (
 <DialogPrimitive.Root modal={modal} {...props} />
)
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Overlay>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Overlay
 ref={ref}
 className={cn(modalOverlayClassName, "z-50", className)}
 {...props}
 />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
 hideClose?: boolean
}

const DialogContent = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Content>,
 DialogContentProps
>(({ className, children, hideClose, onPointerDownOutside, onInteractOutside, onFocusOutside, onOpenAutoFocus, onCloseAutoFocus, ...props }, ref) => {
 const { t } = useTranslation()
 const returnFocusRef = React.useRef<HTMLElement | null>(null)
 const showFallbackTitle = !containsDialogTitle(children)
 const showFallbackDescription =
 !Object.prototype.hasOwnProperty.call(props,"aria-describedby")
 && !containsDialogDescription(children)

 return (
 <DialogPortal>
 <DialogOverlay className="z-[200]" />
 <DialogPrimitive.Content
 ref={ref}
 data-responsive-modal="true"
 className={cn(
"fixed left-[50%] top-[50%] z-[201] flex min-w-0 w-[calc(100vw-1rem)] max-w-lg max-h-[calc(100dvh-1rem)] translate-x-[-50%] translate-y-[-50%] flex-col gap-4 overflow-y-auto overflow-x-hidden overscroll-contain rounded-xl border border-border-subtle bg-popover p-4 text-popover-foreground shadow-modal duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:w-[calc(100vw-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:p-6 dark:border-border-subtle",
 className
 )}
 onPointerDownOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onPointerDownOutside?.(e)
 }}
 onInteractOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onInteractOutside?.(e)
 }}
 onFocusOutside={(e) => {
 if (isReactSelectOutsideEvent(e)) {
 e.preventDefault()
 }
 onFocusOutside?.(e)
 }}
 onOpenAutoFocus={(event) => {
 const activeElement = typeof document !== 'undefined' ? document.activeElement : null
 returnFocusRef.current =
 typeof HTMLElement !== 'undefined' && activeElement instanceof HTMLElement && activeElement !== document.body
 ? activeElement
 : null
 onOpenAutoFocus?.(event)
   if (event.defaultPrevented) return
   const content = event.currentTarget
   if (!(content instanceof HTMLElement)) return
   const initialTarget = content.querySelector<HTMLElement>('[data-dialog-initial-focus]')
   if (!initialTarget) return
   event.preventDefault()
   initialTarget.focus({ preventScroll: true })
 }}
 onCloseAutoFocus={(event) => {
 onCloseAutoFocus?.(event)
 if (event.defaultPrevented) return
 const returnTarget = returnFocusRef.current
 if (!returnTarget?.isConnected) return
 event.preventDefault()
 queueMicrotask(() => {
 if (returnTarget.isConnected) returnTarget.focus({ preventScroll: true })
 })
 }}
 {...props}
 >
 {showFallbackTitle && (
 <DialogPrimitive.Title className="sr-only">
 {t('ui.dialog.fallbackTitle')}
 </DialogPrimitive.Title>
 )}
 {showFallbackDescription && (
 <DialogPrimitive.Description className="sr-only">
 {t('ui.dialog.fallbackDescription')}
 </DialogPrimitive.Description>
 )}
 {children}
 {!hideClose && (
 <DialogPrimitive.Close
 aria-label={t('ui.close')}
 className="absolute end-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-lg border border-destructive/25 bg-destructive/10 text-destructive shadow-sm ring-offset-background transition-colors hover:border-destructive/40 hover:bg-destructive/15 focus:outline-none focus:ring-2 focus:ring-destructive/30 focus:ring-offset-2 focus-visible:ring-offset-2 disabled:pointer-events-none sm:end-6 sm:top-6 dark:bg-destructive/15 dark:hover:bg-destructive/20 dark:ring-offset-background"
 >
 <X className="h-4 w-4" aria-hidden />
 <span className="sr-only">{t('ui.close')}</span>
 </DialogPrimitive.Close>
 )}
 </DialogPrimitive.Content>
 </DialogPortal>
 )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"sticky top-0 z-10 flex min-w-0 flex-col space-y-1.5 bg-popover/95 pe-14 pb-1 text-center sm:pe-16 sm:text-start [&>*]:min-w-0",
 className
 )}
 {...props}
 />
)
DialogHeader.displayName ="DialogHeader"

const DialogFooter = ({
 className,
 ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
 <div
 className={cn(
"sticky bottom-0 z-10 flex min-w-0 flex-col-reverse gap-2 border-t border-border-subtle bg-popover/95 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:flex-row sm:justify-end sm:space-x-2 sm:space-x-reverse [&>*]:min-w-0 [&>*]:max-w-full [&>*]:w-full sm:[&>*]:w-auto",
 className
 )}
 {...props}
 />
)
DialogFooter.displayName ="DialogFooter"

const DialogTitle = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Title>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Title
 ref={ref}
 className={cn(
"break-words text-lg font-semibold leading-tight tracking-tight",
 className
 )}
 {...props}
 />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
 React.ElementRef<typeof DialogPrimitive.Description>,
 React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
 <DialogPrimitive.Description
 ref={ref}
 className={cn("text-sm text-muted-foreground", className)}
 {...props}
 />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
 ({ className, ...props }, ref) => (
 <div
 ref={ref}
 data-responsive-scroll-region="true"
 className={cn('min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain', className)}
 {...props}
 />
 ),
)
DialogBody.displayName = 'DialogBody'

const DialogActions = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
 ({ className, ...props }, ref) => (
 <div
 ref={ref}
 data-responsive-modal-footer="true"
 className={cn(
 'sticky bottom-0 z-10 flex min-w-0 shrink-0 flex-col-reverse gap-2 border-t border-border-subtle bg-popover/95 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:flex-row sm:justify-end sm:space-x-2 sm:space-x-reverse [&>*]:w-full sm:[&>*]:w-auto',
 className,
 )}
 {...props}
 />
 ),
)
DialogActions.displayName = 'DialogActions'

export {
 Dialog,
 DialogPortal,
 DialogOverlay,
 DialogClose,
 DialogTrigger,
 DialogContent,
 DialogHeader,
 DialogFooter,
 DialogTitle,
 DialogDescription,
 DialogBody,
 DialogActions,
}
